/**
 * Experimental Features - Martini Labs
 *
 * Feature flags for experimental functionality that can be toggled via settings.
 * Experimental features are generally OFF by default unless a caller passes
 * an entitlement-aware default.
 */

// Storage keys
// Deprecated: hidden in Preferences; keep runtime toggle during deprecation window.
const VIDEO_THUMBNAILS_KEY = 'martini_labs_video_thumbnails'
const LIGHTING_FILTERS_KEY = 'martini_labs_lighting_filters'
const SET_ASPECT_RATIO_KEY = 'martini_labs_set_aspect_ratio'
const MCP_SERVER_KEY = 'martini_labs_mcp_server'
const VARIATION_STACKS_KEY = 'martini_labs_variation_stacks'
export const COLLECTION_PROPERTY_TYPES_KEY = 'martini_labs_collection_property_types'
const ICONOIR_ICONS_KEY = 'martini_labs_iconoir_icons'
const STRATEGIC_MOUSE_ZOOM_KEY = 'martini_labs_strategic_mouse_zoom'
const CAMERA_MOTION_POINT_PRESENTATION_KEY = 'martini_labs_camera_motion_point_presentation'
export const V10_SET_DEBUG_MODE_KEY = 'martini_labs_v10_set_debug_mode'
export const V10_COLLIDER_OVERLAY_KEY = 'martini_labs_v10_collider_overlay'
export const CONTENT_SHARING_KEY = 'martini_labs_content_sharing'

// Event name for reactivity
export const EXPERIMENTAL_FEATURES_CHANGED_EVENT = 'experimental-features-changed'

/** File sharing is an opt-in developer preview. */
export function isContentSharingEnabled(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(CONTENT_SHARING_KEY) === 'true'
}

export function setContentSharingEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(CONTENT_SHARING_KEY, String(enabled))
  window.dispatchEvent(
    new CustomEvent(EXPERIMENTAL_FEATURES_CHANGED_EVENT, { detail: { feature: 'contentSharing', enabled } }),
  )
}

export const CAMERA_MOTION_POINT_PRESENTATIONS = [
  'koh',
  'independent-additive-glow',
  'normal-soft-dots',
  'screen-soft-dots',
  'alpha-hashed-dots',
  'opaque-tiny-discs',
  'soft-capped-alpha',
  'depth-faded-points',
  'density-capped',
  'base-plus-sparse-sparkle',
] as const

export type CameraMotionPointPresentation = (typeof CAMERA_MOTION_POINT_PRESENTATIONS)[number]

export const DEFAULT_CAMERA_MOTION_POINT_PRESENTATION: CameraMotionPointPresentation = 'koh'

function isCameraMotionPointPresentation(value: string | null): value is CameraMotionPointPresentation {
  return CAMERA_MOTION_POINT_PRESENTATIONS.includes(value as CameraMotionPointPresentation)
}

/**
 * Get video thumbnails enabled state (hover preview on canvas nodes)
 * @deprecated Hidden from Preferences; pending removal after deprecation window.
 * @returns false by default
 */
export function isVideoThumbnailsEnabled(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(VIDEO_THUMBNAILS_KEY) === 'true'
}

/**
 * Set video thumbnails enabled state
 * @deprecated Hidden from Preferences; pending removal after deprecation window.
 */
export function setVideoThumbnailsEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(VIDEO_THUMBNAILS_KEY, String(enabled))
  window.dispatchEvent(
    new CustomEvent(EXPERIMENTAL_FEATURES_CHANGED_EVENT, {
      detail: { feature: 'videoThumbnails', enabled },
    }),
  )
}

/**
 * Set lighting filters enabled state
 */
export function setLightingFiltersEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(LIGHTING_FILTERS_KEY, String(enabled))
  window.dispatchEvent(
    new CustomEvent(EXPERIMENTAL_FEATURES_CHANGED_EVENT, {
      detail: { feature: 'lightingFilters', enabled },
    }),
  )
}

/**
 * Get set aspect ratio selector enabled state
 * @returns false by default (aspect ratio locked to 16:9)
 */
export function isSetAspectRatioEnabled(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(SET_ASPECT_RATIO_KEY) === 'true'
}

/**
 * Set set aspect ratio selector enabled state
 */
export function setSetAspectRatioEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(SET_ASPECT_RATIO_KEY, String(enabled))
  window.dispatchEvent(
    new CustomEvent(EXPERIMENTAL_FEATURES_CHANGED_EVENT, {
      detail: { feature: 'setAspectRatio', enabled },
    }),
  )
}

/**
 * Get MCP server visibility enabled state
 * @returns false by default
 */
export function isMcpServerEnabled(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(MCP_SERVER_KEY) === 'true'
}

/**
 * Set MCP server visibility enabled state
 */
export function setMcpServerEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(MCP_SERVER_KEY, String(enabled))
  window.dispatchEvent(
    new CustomEvent(EXPERIMENTAL_FEATURES_CHANGED_EVENT, {
      detail: { feature: 'mcpServer', enabled },
    }),
  )
}

/**
 * Get audio nodes enabled state
 * @returns true for all users
 */
export function isAudioNodesEnabled(): boolean {
  return true
}

/**
 * Set audio nodes enabled state
 * @deprecated Audio nodes are released to everyone; this setter is retained for old callers only.
 */
export function setAudioNodesEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent(EXPERIMENTAL_FEATURES_CHANGED_EVENT, {
      detail: { feature: 'audioNodes', enabled },
    }),
  )
}

/**
 * Get variation stacks enabled state
 * @returns false by default
 */
export function isVariationStacksEnabled(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(VARIATION_STACKS_KEY) === 'true'
}

/**
 * Set variation stacks enabled state
 */
export function setVariationStacksEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(VARIATION_STACKS_KEY, String(enabled))
  window.dispatchEvent(
    new CustomEvent(EXPERIMENTAL_FEATURES_CHANGED_EVENT, {
      detail: { feature: 'variationStacks', enabled },
    }),
  )
}

/**
 * Get collection property type picker enabled state.
 * @returns false by default (new properties are created as text fields)
 */
export function isCollectionPropertyTypesEnabled(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(COLLECTION_PROPERTY_TYPES_KEY) === 'true'
}

/**
 * Set collection property type picker enabled state.
 */
export function setCollectionPropertyTypesEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(COLLECTION_PROPERTY_TYPES_KEY, String(enabled))
  window.dispatchEvent(
    new CustomEvent(EXPERIMENTAL_FEATURES_CHANGED_EVENT, {
      detail: { feature: 'collectionPropertyTypes', enabled },
    }),
  )
}

/**
 * Get iconoir icons enabled state.
 *
 * When on, icons rendered through `@/components/ui/icons` use the iconoir-react
 * pack instead of lucide-react. Both packs ship while the flag is live.
 * @returns false by default
 */
export function isIconoirIconsEnabled(): boolean {
  if (typeof window === 'undefined') return false
  if (typeof localStorage === 'undefined') return false
  return localStorage.getItem(ICONOIR_ICONS_KEY) === 'true'
}

/**
 * Set iconoir icons enabled state
 */
export function setIconoirIconsEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(ICONOIR_ICONS_KEY, String(enabled))
  window.dispatchEvent(
    new CustomEvent(EXPERIMENTAL_FEATURES_CHANGED_EVENT, {
      detail: { feature: 'iconoirIcons', enabled },
    }),
  )
}

/**
 * Camera-control V1 is generally available. Keep the old setter/key so stale
 * clients and internal tooling do not crash, but do not let local rollout state
 * suppress camera-motion nodes anymore.
 */
export function isCameraControlV1Enabled(): boolean {
  return true
}

export function getCameraMotionPointPresentation(): CameraMotionPointPresentation {
  if (typeof window === 'undefined') return DEFAULT_CAMERA_MOTION_POINT_PRESENTATION
  if (typeof localStorage === 'undefined') return DEFAULT_CAMERA_MOTION_POINT_PRESENTATION
  const stored = localStorage.getItem(CAMERA_MOTION_POINT_PRESENTATION_KEY)
  return isCameraMotionPointPresentation(stored) ? stored : DEFAULT_CAMERA_MOTION_POINT_PRESENTATION
}

export function setCameraMotionPointPresentation(value: CameraMotionPointPresentation): void {
  if (typeof window === 'undefined') return
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(CAMERA_MOTION_POINT_PRESENTATION_KEY, value)
  window.dispatchEvent(
    new CustomEvent(EXPERIMENTAL_FEATURES_CHANGED_EVENT, {
      detail: { feature: 'cameraMotionPointPresentation', value },
    }),
  )
}

/**
 * Whether the current browser/OS combo is supported by Strategic Mouse Zoom.
 *
 * The feature's mouse-vs-trackpad discrimination is built on the non-standard
 * `wheelDeltaY` property (Chrome/Safari only) and was tuned against Chrome on
 * macOS specifically — its trackpad gestures and wheel-smoothing behavior set
 * the heuristic thresholds. Other browsers and OSes emit different signatures
 * and would need separate validation, so the toggle is hidden everywhere else.
 */
export function isStrategicMouseZoomSupported(): boolean {
  if (typeof navigator === 'undefined') return false
  // Prefer the modern userAgentData.platform; navigator.platform is deprecated
  // and Chrome suppresses it in some privacy-sensitive contexts. Fall back to
  // navigator.platform for older Chromium versions that lack userAgentData.
  const uaDataPlatform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform
  const platform = uaDataPlatform ?? navigator.platform ?? ''
  const isMac = /mac/i.test(platform)
  const ua = navigator.userAgent
  const isChrome = /Chrome\//.test(ua) && !/Edg\//.test(ua) && !/OPR\//.test(ua)
  return isMac && isChrome
}

/**
 * Get strategic mouse zoom enabled state.
 *
 * When enabled, mouse-wheel input is remapped to a Supreme-Commander-style
 * scheme: unmodified wheel zooms, Cmd+wheel pans Y, Cmd+Shift+wheel pans X
 * (Ctrl works as a Cmd alias for muscle-memory parity with the PC original).
 * Trackpad input is unaffected.
 *
 * Always returns false on browsers/OSes where the feature isn't supported,
 * even if the localStorage flag was set from a prior session.
 *
 * @returns false by default
 */
export function isStrategicMouseZoomEnabled(): boolean {
  if (typeof window === 'undefined') return false
  if (!isStrategicMouseZoomSupported()) return false
  return localStorage.getItem(STRATEGIC_MOUSE_ZOOM_KEY) === 'true'
}

/**
 * Set strategic mouse zoom enabled state
 */
export function setStrategicMouseZoomEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(STRATEGIC_MOUSE_ZOOM_KEY, String(enabled))
  window.dispatchEvent(
    new CustomEvent(EXPERIMENTAL_FEATURES_CHANGED_EVENT, {
      detail: { feature: 'strategicMouseZoom', enabled },
    }),
  )
}

/** Dev-only inline V10 set debug panel. Off by default. */
export function isV10SetDebugModeEnabled(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(V10_SET_DEBUG_MODE_KEY) === 'true'
}

export function setV10SetDebugModeEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(V10_SET_DEBUG_MODE_KEY, String(enabled))
  window.dispatchEvent(
    new CustomEvent(EXPERIMENTAL_FEATURES_CHANGED_EVENT, {
      detail: { feature: 'v10SetDebugMode', enabled },
    }),
  )
}

/** Dev-only collider wireframe overlay for splat/collider alignment QA. */
export function isV10ColliderOverlayEnabled(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(V10_COLLIDER_OVERLAY_KEY) === 'true'
}

export function setV10ColliderOverlayEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(V10_COLLIDER_OVERLAY_KEY, String(enabled))
  window.dispatchEvent(
    new CustomEvent(EXPERIMENTAL_FEATURES_CHANGED_EVENT, {
      detail: { feature: 'v10ColliderOverlay', enabled },
    }),
  )
}

// Onboarding v2 — intent-driven first-project flow on /welcome (contract C6,
// docs/plans/onboarding-v2-contracts.md). Rollout follows the canvas-v3
// pattern: explicit localStorage preference wins, otherwise the
// ONBOARDING_V2_DEFAULT constant in lib/onboarding/gate.ts decides
// (flipped ON at Stage 2).
export const ONBOARDING_V2_KEY = 'martini_labs_onboarding_v2'

/**
 * Get onboarding v2 enabled state.
 *
 * When the user hasn't explicitly set the flag, falls back to `defaultValue`.
 */
export function isOnboardingV2Enabled(defaultValue = false): boolean {
  if (typeof window === 'undefined') return defaultValue
  const stored = localStorage.getItem(ONBOARDING_V2_KEY)
  if (stored === null) return defaultValue
  return stored === 'true'
}

/**
 * Set onboarding v2 enabled state
 */
export function setOnboardingV2Enabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(ONBOARDING_V2_KEY, String(enabled))
  window.dispatchEvent(
    new CustomEvent(EXPERIMENTAL_FEATURES_CHANGED_EVENT, {
      detail: { feature: 'onboardingV2', enabled },
    }),
  )
}

// Onboarding "motion" variant — "Step into Set" first-project flow on /welcome.
// The rollout default lives in lib/onboarding/gate.ts. Explicit localStorage
// preference remains as a deterministic E2E/development override.
export const ONBOARDING_MOTION_KEY = 'martini_labs_onboarding_motion'

/**
 * Get onboarding "motion" variant enabled state.
 *
 * When the user hasn't explicitly set the flag, falls back to `defaultValue`.
 */
export function isOnboardingMotionEnabled(defaultValue = false): boolean {
  if (typeof window === 'undefined') return defaultValue
  const stored = localStorage.getItem(ONBOARDING_MOTION_KEY)
  if (stored === null) return defaultValue
  return stored === 'true'
}

const ONBOARDING_V2_DEFAULT_APPLIED_KEY = 'martini_onboarding_v2_default_applied'

/**
 * Tracks whether we've already fired the `onboarding_v2_default_applied`
 * analytics event for this device, so it stays a per-device-once event.
 * Returns true the first time it's called; false on subsequent calls.
 * (Cohort-sizing helper for the Stage-2 default flip — P9 wires the call.)
 */
export function consumeOnboardingV2DefaultAppliedFlag(): boolean {
  if (typeof window === 'undefined') return false
  if (localStorage.getItem(ONBOARDING_V2_DEFAULT_APPLIED_KEY) === 'true') return false
  localStorage.setItem(ONBOARDING_V2_DEFAULT_APPLIED_KEY, 'true')
  return true
}

const ONBOARDING_MOTION_DEFAULT_APPLIED_KEY = 'martini_onboarding_motion_default_applied'

/**
 * Tracks whether we've already fired the `onboarding_motion_default_applied`
 * analytics event for this device, so it stays a per-device-once event.
 * Returns true the first time it's called; false on subsequent calls.
 * (Cohort-sizing helper for the motion-onboarding default flip.)
 */
export function consumeOnboardingMotionDefaultAppliedFlag(): boolean {
  if (typeof window === 'undefined') return false
  if (localStorage.getItem(ONBOARDING_MOTION_DEFAULT_APPLIED_KEY) === 'true') return false
  localStorage.setItem(ONBOARDING_MOTION_DEFAULT_APPLIED_KEY, 'true')
  return true
}
